import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useOutletContext, useNavigate } from 'react-router-dom';
import {
  Search,
  Filter,
  Download,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  Clock,
  Eye,
  Edit,
  DollarSign,
  User,
  Calendar,
  Mail,
  Phone,
  Package,
  CheckCircle,
  XCircle,
  Truck,
  FileText,
  Printer,
  ScanLine,
  Frame,
  Palette,
  MoreHorizontal,
  LayoutGrid,
  List
} from 'lucide-react';
import { supabase } from '../../utils/supabaseClient';

const STATUS_CONFIG = {
  submitted:     { label: 'Submitted',     color: '#6366f1', bg: '#eef2ff' },
  reviewing:     { label: 'Reviewing',     color: '#8b5cf6', bg: '#f5f3ff' },
  quoted:        { label: 'Quoted',        color: '#f59e0b', bg: '#fffbeb' },
  approved:      { label: 'Approved',      color: '#3b82f6', bg: '#eff6ff' },
  in_production: { label: 'In Production', color: '#ec4899', bg: '#fdf2f8' },
  quality_check: { label: 'QC',            color: '#8b5cf6', bg: '#f5f3ff' },
  ready:         { label: 'Ready',         color: '#10b981', bg: '#ecfdf5' },
  delivered:     { label: 'Delivered',      color: '#059669', bg: '#d1fae5' },
  invoiced:      { label: 'Invoiced',      color: '#f97316', bg: '#fff7ed' },
  paid:          { label: 'Paid',          color: '#22c55e', bg: '#f0fdf4' },
  cancelled:     { label: 'Cancelled',     color: '#ef4444', bg: '#fef2f2' },
};

const SERVICE_ICONS = {
  printing: '🖨',
  scanning: '📷',
  reproduction: '🎨',
  film_processing: '🎞',
  framing: '🖼',
  other: '✦',
};

const SERVICE_LABELS = {
  printing: 'Printing',
  scanning: 'Scanning',
  reproduction: 'Reproduction',
  film_processing: 'Film Processing',
  framing: 'Framing',
  other: 'Other',
};

const CuratorServiceOrders = () => {
  const { selectedOrganization } = useOutletContext() || {};
  const orgId = selectedOrganization?.organization_id;
  const navigate = useNavigate();

  const [orders, setOrders] = useState([]);
  const [allOrders, setAllOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [viewMode, setViewMode] = useState('list'); // 'board' or 'list'
  const [stats, setStats] = useState({
    total: 0, submitted: 0, in_production: 0, ready: 0, paid: 0, totalRevenue: 0
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [serviceFilter, setServiceFilter] = useState('all');
  const [sortBy, setSortBy] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');
  const [pageSize] = useState(50);
  const [totalCount, setTotalCount] = useState(0);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [updating, setUpdating] = useState(false);
  const listEndRef = useRef(null);

  // Fetch orders with infinite scroll (append mode)
  const fetchOrders = useCallback(async (reset = true) => {
    if (!orgId) return;
    if (reset) {
      setLoading(true);
      setOrders([]);
      setHasMore(true);
    } else {
      setLoadingMore(true);
    }
    try {
      const offset = reset ? 0 : orders.length;
      let query = supabase
        .from('service_orders')
        .select('*', { count: 'exact' })
        .eq('organization_id', orgId)
        .order(sortBy, { ascending: sortOrder === 'asc' })
        .range(offset, offset + pageSize - 1);

      if (statusFilter !== 'all') query = query.eq('status', statusFilter);
      if (serviceFilter !== 'all') query = query.eq('service_type', serviceFilter);
      if (searchQuery) {
        query = query.or(`customer_name.ilike.%${searchQuery}%,customer_email.ilike.%${searchQuery}%,order_num.ilike.%${searchQuery}%`);
      }

      const { data, error, count } = await query;
      if (error) throw error;

      const newData = data || [];
      if (reset) {
        setOrders(newData);
      } else {
        setOrders(prev => [...prev, ...newData]);
      }
      setTotalCount(count || 0);
      setHasMore(newData.length === pageSize);
    } catch (err) {
      console.error('Failed to fetch service orders:', err);
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  }, [orgId, statusFilter, serviceFilter, searchQuery, sortBy, sortOrder, pageSize]);

  // Stats via individual count queries to avoid 1000-row limit
  const fetchStats = useCallback(async () => {
    if (!orgId) return;
    try {
      const [totalRes, newRes, prodRes, readyRes, paidRes, revenueRes] = await Promise.all([
        supabase.from('service_orders').select('*', { count: 'exact', head: true }).eq('organization_id', orgId),
        supabase.from('service_orders').select('*', { count: 'exact', head: true }).eq('organization_id', orgId).in('status', ['submitted', 'reviewing']),
        supabase.from('service_orders').select('*', { count: 'exact', head: true }).eq('organization_id', orgId).in('status', ['in_production', 'quality_check']),
        supabase.from('service_orders').select('*', { count: 'exact', head: true }).eq('organization_id', orgId).eq('status', 'ready'),
        supabase.from('service_orders').select('*', { count: 'exact', head: true }).eq('organization_id', orgId).eq('status', 'paid'),
        supabase.from('service_orders').select('final_price, quoted_price').eq('organization_id', orgId).eq('status', 'paid').limit(10000),
      ]);

      const revenue = (revenueRes.data || []).reduce((sum, o) => sum + parseFloat(o.final_price || o.quoted_price || 0), 0);

      setStats({
        total: totalRes.count || 0,
        submitted: newRes.count || 0,
        in_production: prodRes.count || 0,
        ready: readyRes.count || 0,
        paid: paidRes.count || 0,
        totalRevenue: revenue,
      });
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    }
  }, [orgId]);

  useEffect(() => { fetchOrders(true); fetchStats(); }, [fetchStats, statusFilter, serviceFilter, searchQuery, sortBy, sortOrder, orgId]);

  // Infinite scroll observer
  useEffect(() => {
    if (viewMode !== 'list' || !listEndRef.current) return;
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting && hasMore && !loading && !loadingMore) fetchOrders(false); },
      { threshold: 0.1 }
    );
    observer.observe(listEndRef.current);
    return () => observer.disconnect();
  }, [viewMode, hasMore, loading, loadingMore, fetchOrders]);

  // Fetch ACTIVE orders for board view (exclude archived statuses)
  const ACTIVE_STATUSES = ['submitted', 'reviewing', 'quoted', 'approved', 'in_production', 'quality_check', 'ready', 'delivered'];
  const fetchAllOrders = useCallback(async () => {
    if (!orgId) return;
    try {
      const { data, error } = await supabase
        .from('service_orders')
        .select('*')
        .eq('organization_id', orgId)
        .in('status', ACTIVE_STATUSES)
        .order('created_at', { ascending: false });
      if (error) throw error;
      setAllOrders(data || []);
    } catch (err) {
      console.error('Failed to fetch board orders:', err);
    }
  }, [orgId]);

  useEffect(() => { if (viewMode === 'board') fetchAllOrders(); }, [viewMode, fetchAllOrders]);

  // Update order status (used by board drag-drop)
  const updateStatus = async (orderId, newStatus) => {
    setUpdating(true);
    try {
      const updates = { status: newStatus };
      if (newStatus === 'quoted') updates.quoted_at = new Date().toISOString();
      if (newStatus === 'approved') updates.approved_at = new Date().toISOString();
      if (newStatus === 'delivered') updates.completed_at = new Date().toISOString();
      if (newStatus === 'paid') updates.paid_at = new Date().toISOString();

      const { error } = await supabase.from('service_orders').update(updates).eq('id', orderId);
      if (error) throw error;

      setOrders(prev => prev.map(o => o.id === orderId ? { ...o, ...updates } : o));
      fetchStats();
    } catch (err) {
      console.error('Failed to update status:', err);
      alert('Failed to update status: ' + err.message);
    } finally {
      setUpdating(false);
    }
  };

  const navigateToOrder = (order) => navigate(`/curator/service-orders/${order.id}`);

  // Export CSV
  const exportCSV = () => {
    const headers = ['Order #', 'Date', 'Customer', 'Email', 'Phone', 'Service', 'Tier', 'Paper', 'Size', 'Qty', 'Status', 'Quoted', 'Final', 'Discount', 'Notes'];
    const rows = orders.map(o => [
      o.order_num, new Date(o.created_at).toLocaleDateString(), o.customer_name, o.customer_email,
      o.customer_phone || '', SERVICE_LABELS[o.service_type], o.tier || '', o.paper_type || '',
      o.size_width && o.size_height ? `${o.size_width}x${o.size_height}` : '', o.quantity,
      o.status, o.quoted_price || '', o.final_price || '', o.discount_percent ? `${o.discount_percent}%` : '', o.notes || ''
    ]);
    const csv = [headers, ...rows].map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `printlab-orders-${new Date().toISOString().split('T')[0]}.csv`;
    a.click(); URL.revokeObjectURL(url);
  };

  const handleSort = (col) => {
    if (sortBy === col) { setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc'); }
    else { setSortBy(col); setSortOrder('desc'); }
  };

  const SortIcon = ({ col }) => sortBy === col
    ? (sortOrder === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />)
    : null;

  return (
    <div style={{ padding: '24px', maxWidth: 1400, margin: '0 auto' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: '1.5rem', fontWeight: 600, color: '#1a1a2e', margin: 0 }}>PrintLab Orders</h1>
          <p style={{ fontSize: '.85rem', color: '#666', marginTop: 4 }}>Manage print, scan, and reproduction orders</p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <div style={{ display: 'flex', borderRadius: 6, border: '1px solid #e5e7eb', overflow: 'hidden' }}>
            <button onClick={() => setViewMode('board')}
              style={{ ...btnStyle, borderRadius: 0, border: 'none', background: viewMode === 'board' ? '#4f46e5' : '#fff', color: viewMode === 'board' ? '#fff' : '#333' }}>
              <LayoutGrid size={16} /> Board
            </button>
            <button onClick={() => setViewMode('list')}
              style={{ ...btnStyle, borderRadius: 0, border: 'none', borderLeft: '1px solid #e5e7eb', background: viewMode === 'list' ? '#4f46e5' : '#fff', color: viewMode === 'list' ? '#fff' : '#333' }}>
              <List size={16} /> List
            </button>
          </div>
          <button onClick={exportCSV} style={btnStyle}><Download size={16} /> Export</button>
          <button onClick={() => { fetchOrders(); fetchStats(); if (viewMode === 'board') fetchAllOrders(); }} style={btnStyle}><RefreshCw size={16} /> Refresh</button>
        </div>
      </div>

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 16, marginBottom: 24 }}>
        <StatCard label="Total Orders" value={stats.total} icon={<FileText size={20} />} color="#6366f1" />
        <StatCard label="New / Reviewing" value={stats.submitted} icon={<Clock size={20} />} color="#f59e0b" />
        <StatCard label="In Production" value={stats.in_production} icon={<Printer size={20} />} color="#ec4899" />
        <StatCard label="Ready for Pickup" value={stats.ready} icon={<Package size={20} />} color="#10b981" />
        <StatCard label="Revenue (Paid)" value={`$${stats.totalRevenue.toLocaleString('en-US', { minimumFractionDigits: 2 })}`} icon={<DollarSign size={20} />} color="#22c55e" />
      </div>

      {/* Board View */}
      {viewMode === 'board' && (
        <KanbanBoard
          orders={allOrders}
          orgId={orgId}
          onStatusChange={async (orderId, newStatus) => {
            await updateStatus(orderId, newStatus);
            fetchAllOrders();
          }}
          onCardClick={(order) => navigateToOrder(order)}
          updating={updating}
        />
      )}

      {/* List View */}
      {viewMode === 'list' && <>
      {/* Search & Filters */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 16, alignItems: 'center' }}>
        <div style={{ flex: 1, position: 'relative' }}>
          <Search size={16} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: '#999' }} />
          <input
            type="text" placeholder="Search by name, email, or order #..."
            value={searchQuery} onChange={e => { setSearchQuery(e.target.value); }}
            style={{ ...inputStyle, paddingLeft: 36, width: '100%' }}
          />
        </div>
        <select value={statusFilter} onChange={e => { setStatusFilter(e.target.value); }} style={inputStyle}>
          <option value="all">All Statuses</option>
          {Object.entries(STATUS_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
        <select value={serviceFilter} onChange={e => { setServiceFilter(e.target.value); }} style={inputStyle}>
          <option value="all">All Services</option>
          {Object.entries(SERVICE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
      </div>

      {/* Orders Table */}
      <div style={{ background: '#fff', borderRadius: 8, border: '1px solid #e5e7eb', overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '.85rem' }}>
          <thead>
            <tr style={{ background: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
              <Th onClick={() => handleSort('order_num')}>Order # <SortIcon col="order_num" /></Th>
              <Th onClick={() => handleSort('created_at')}>Date <SortIcon col="created_at" /></Th>
              <Th>Customer</Th>
              <Th>Service</Th>
              <Th>Details</Th>
              <Th onClick={() => handleSort('status')}>Status <SortIcon col="status" /></Th>
              <Th onClick={() => handleSort('quoted_price')}>Price <SortIcon col="quoted_price" /></Th>
              <Th>Actions</Th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={8} style={{ padding: 40, textAlign: 'center', color: '#999' }}>Loading...</td></tr>
            ) : orders.length === 0 ? (
              <tr><td colSpan={8} style={{ padding: 40, textAlign: 'center', color: '#999' }}>No orders found</td></tr>
            ) : orders.map(order => (
              <tr key={order.id} style={{ borderBottom: '1px solid #f3f4f6', cursor: 'pointer' }}
                  onClick={() => navigateToOrder(order)}>
                <td style={tdStyle}><span style={{ fontWeight: 500, color: '#4f46e5' }}>{order.order_num}</span></td>
                <td style={tdStyle}>{new Date(order.created_at).toLocaleDateString()}</td>
                <td style={tdStyle}>
                  <div style={{ fontWeight: 500 }}>{order.customer_name}</div>
                  <div style={{ fontSize: '.75rem', color: '#999' }}>{order.customer_email}</div>
                </td>
                <td style={tdStyle}>
                  <span style={{ marginRight: 6 }}>{SERVICE_ICONS[order.service_type]}</span>
                  {SERVICE_LABELS[order.service_type]}
                  {order.tier && <div style={{ fontSize: '.72rem', color: '#999' }}>{order.tier}</div>}
                </td>
                <td style={tdStyle}>
                  {order.size_width && order.size_height && <div>{order.size_width}×{order.size_height}″</div>}
                  {order.paper_type && <div style={{ fontSize: '.72rem', color: '#999' }}>{order.paper_type}</div>}
                  {order.quantity > 1 && <div style={{ fontSize: '.72rem', color: '#666' }}>Qty: {order.quantity}</div>}
                </td>
                <td style={tdStyle}><StatusBadge status={order.status} /></td>
                <td style={tdStyle}>
                  {order.final_price ? `$${parseFloat(order.final_price).toFixed(2)}`
                    : order.quoted_price ? <span style={{ color: '#999' }}>${parseFloat(order.quoted_price).toFixed(2)} (quote)</span>
                    : '—'}
                  {order.discount_percent > 0 && (
                    <div style={{ fontSize: '.68rem', color: '#b0957e' }}>-{order.discount_percent}% artist</div>
                  )}
                </td>
                <td style={tdStyle} onClick={e => e.stopPropagation()}>
                  <button onClick={() => navigateToOrder(order)}
                    style={{ ...btnSmall, marginRight: 4 }}><Eye size={14} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Infinite scroll sentinel + status */}
      <div ref={listEndRef} style={{ padding: '16px 0', textAlign: 'center' }}>
        {loadingMore && <div style={{ fontSize: '.85rem', color: '#6b7280' }}>Loading more orders...</div>}
        {!hasMore && orders.length > 0 && (
          <div style={{ fontSize: '.8rem', color: '#9ca3af' }}>Showing all {totalCount.toLocaleString()} orders</div>
        )}
      </div>

      </>}
    </div>
  );
};

/* ---------- Kanban Board ---------- */

const BOARD_COLUMNS = [
  { key: 'submitted', label: 'Submitted' },
  { key: 'reviewing', label: 'Reviewing' },
  { key: 'quoted', label: 'Quoted' },
  { key: 'approved', label: 'Approved' },
  { key: 'in_production', label: 'In Production' },
  { key: 'quality_check', label: 'QC' },
  { key: 'ready', label: 'Ready' },
  { key: 'delivered', label: 'Delivered' },
];

const ARCHIVE_STATUSES = ['invoiced', 'paid', 'cancelled'];

const KanbanBoard = ({ orders, onStatusChange, onCardClick, updating, orgId }) => {
  const [draggedOrder, setDraggedOrder] = useState(null);
  const [dragOverCol, setDragOverCol] = useState(null);
  const [showArchive, setShowArchive] = useState(false);
  const [archiveOrders, setArchiveOrders] = useState([]);
  const [archiveCount, setArchiveCount] = useState(0);
  const [loadingArchive, setLoadingArchive] = useState(false);
  const boardRef = useRef(null);

  const columnOrders = {};
  BOARD_COLUMNS.forEach(col => { columnOrders[col.key] = []; });

  orders.forEach(order => {
    if (columnOrders[order.status]) {
      columnOrders[order.status].push(order);
    }
  });

  // Fetch archive count on mount
  useEffect(() => {
    if (!orgId) return;
    supabase.from('service_orders').select('*', { count: 'exact', head: true })
      .eq('organization_id', orgId).in('status', ARCHIVE_STATUSES)
      .then(({ count }) => setArchiveCount(count || 0));
  }, [orgId, orders]);

  // Lazy-load archive orders when expanded
  const loadArchive = async () => {
    if (archiveOrders.length > 0) { setShowArchive(!showArchive); return; }
    setShowArchive(true);
    setLoadingArchive(true);
    try {
      const { data, error } = await supabase
        .from('service_orders')
        .select('*')
        .eq('organization_id', orgId)
        .in('status', ARCHIVE_STATUSES)
        .order('created_at', { ascending: false })
        .limit(200);
      if (error) throw error;
      setArchiveOrders(data || []);
    } catch (err) {
      console.error('Failed to load archive:', err);
    } finally {
      setLoadingArchive(false);
    }
  };

  const handleDragStart = (e, order) => {
    setDraggedOrder(order);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', order.id);
  };

  const handleDragOver = (e, colKey) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverCol(colKey);
  };

  const handleDragLeave = () => {
    setDragOverCol(null);
  };

  const handleDrop = async (e, colKey) => {
    e.preventDefault();
    setDragOverCol(null);
    if (draggedOrder && draggedOrder.status !== colKey) {
      await onStatusChange(draggedOrder.id, colKey);
    }
    setDraggedOrder(null);
  };

  const formatDate = (d) => {
    if (!d) return '';
    const date = new Date(d);
    return `${date.getMonth() + 1}/${date.getDate()}`;
  };

  return (
    <div>
      <div ref={boardRef} style={{
        display: 'flex', gap: 12, overflowX: 'auto', paddingBottom: 16,
        minHeight: 500,
      }}>
        {BOARD_COLUMNS.map(col => {
          const cfg = STATUS_CONFIG[col.key];
          const colItems = columnOrders[col.key] || [];
          const isDragTarget = dragOverCol === col.key;

          return (
            <div key={col.key}
              onDragOver={(e) => handleDragOver(e, col.key)}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, col.key)}
              style={{
                flex: '0 0 220px', minWidth: 220,
                background: isDragTarget ? `${cfg.color}10` : '#f9fafb',
                borderRadius: 10,
                border: isDragTarget ? `2px dashed ${cfg.color}` : '2px solid transparent',
                padding: 10,
                transition: 'all .15s',
                display: 'flex', flexDirection: 'column',
              }}>
              {/* Column header */}
              <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                marginBottom: 10, padding: '6px 8px',
                borderRadius: 6, background: cfg.bg,
              }}>
                <span style={{ fontSize: '.78rem', fontWeight: 600, color: cfg.color }}>{col.label}</span>
                <span style={{
                  fontSize: '.7rem', fontWeight: 600, color: cfg.color,
                  background: '#fff', borderRadius: 10, padding: '2px 8px',
                  minWidth: 20, textAlign: 'center',
                }}>{colItems.length}</span>
              </div>

              {/* Cards */}
              <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 8 }}>
                {colItems.length === 0 && (
                  <div style={{ padding: 16, textAlign: 'center', fontSize: '.75rem', color: '#bbb', fontStyle: 'italic' }}>
                    No orders
                  </div>
                )}
                {colItems.map(order => (
                  <KanbanCard key={order.id} order={order} cfg={cfg}
                    onDragStart={handleDragStart} onClick={() => onCardClick(order)}
                    isDragging={draggedOrder?.id === order.id} />
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {/* Archive toggle */}
      {archiveCount > 0 && (
        <div style={{ marginTop: 16 }}>
          <button onClick={loadArchive}
            style={{
              display: 'flex', alignItems: 'center', gap: 8,
              background: 'none', border: 'none', cursor: 'pointer',
              fontSize: '.82rem', color: '#6b7280', fontWeight: 500,
            }}>
            {showArchive ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
            Completed / Archived ({archiveCount.toLocaleString()})
          </button>
          {showArchive && (
            <div style={{
              display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 10,
              padding: 16, background: '#f9fafb', borderRadius: 10,
              maxHeight: 300, overflowY: 'auto',
            }}>
              {loadingArchive ? (
                <div style={{ padding: 16, textAlign: 'center', color: '#6b7280', width: '100%' }}>Loading archived orders...</div>
              ) : archiveOrders.map(order => {
                const cfg = STATUS_CONFIG[order.status] || STATUS_CONFIG.paid;
                return (
                  <div key={order.id} onClick={() => onCardClick(order)} style={{
                    background: '#fff', borderRadius: 8, padding: '10px 14px',
                    border: '1px solid #e5e7eb', cursor: 'pointer', width: 200,
                    fontSize: '.78rem', transition: 'box-shadow .15s',
                  }}>
                    <div style={{ fontWeight: 600, color: '#4f46e5', fontSize: '.72rem' }}>{order.order_num}</div>
                    <div style={{ fontWeight: 500, marginTop: 2 }}>{order.customer_name}</div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4, alignItems: 'center' }}>
                      <span style={{
                        padding: '2px 8px', borderRadius: 10, fontSize: '.68rem',
                        fontWeight: 500, color: cfg.color, background: cfg.bg,
                      }}>{cfg.label}</span>
                      <span style={{ fontSize: '.72rem', color: '#999' }}>{formatDate(order.created_at)}</span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const KanbanCard = ({ order, cfg, onDragStart, onClick, isDragging }) => {
  const price = parseFloat(order.final_price || order.quoted_price || 0);
  const formatDate = (d) => { if (!d) return ''; const dt = new Date(d); return `${dt.getMonth()+1}/${dt.getDate()}`; };

  return (
    <div
      draggable
      onDragStart={(e) => onDragStart(e, order)}
      onClick={onClick}
      style={{
        background: '#fff', borderRadius: 8, padding: 12,
        border: '1px solid #e5e7eb',
        borderLeft: `3px solid ${cfg.color}`,
        cursor: 'grab', opacity: isDragging ? 0.4 : 1,
        transition: 'all .15s', fontSize: '.78rem',
        boxShadow: isDragging ? '0 4px 12px rgba(0,0,0,.15)' : '0 1px 3px rgba(0,0,0,.05)',
      }}>
      {/* Order number */}
      <div style={{ fontWeight: 600, color: '#4f46e5', fontSize: '.72rem', marginBottom: 4 }}>
        {order.order_num}
      </div>
      {/* Customer */}
      <div style={{ fontWeight: 500, color: '#111827', marginBottom: 4 }}>
        {order.customer_name}
      </div>
      {/* Service + paper */}
      <div style={{ color: '#6b7280', fontSize: '.72rem', marginBottom: 6 }}>
        {SERVICE_ICONS[order.service_type]} {SERVICE_LABELS[order.service_type]}
        {order.paper_type && <span> · {order.paper_type.length > 20 ? order.paper_type.slice(0, 18) + '…' : order.paper_type}</span>}
      </div>
      {/* Footer: date + price */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <span style={{ fontSize: '.7rem', color: '#9ca3af' }}>{formatDate(order.created_at)}</span>
        {price > 0 && <span style={{ fontSize: '.72rem', fontWeight: 600, color: '#059669' }}>${price.toFixed(0)}</span>}
        {order.quantity > 1 && <span style={{ fontSize: '.68rem', color: '#9ca3af' }}>×{order.quantity}</span>}
      </div>
      {/* Deadline warning */}
      {order.deadline && new Date(order.deadline) < new Date(Date.now() + 2 * 86400000) && (
        <div style={{ marginTop: 4, fontSize: '.68rem', color: '#ef4444', fontWeight: 500 }}>
          ⚠ Due {formatDate(order.deadline)}
        </div>
      )}
    </div>
  );
};

/* ---------- Sub-components ---------- */

const StatCard = ({ label, value, icon, color }) => (
  <div style={{
    background: '#fff', borderRadius: 8, border: '1px solid #e5e7eb',
    padding: 20, display: 'flex', alignItems: 'center', gap: 16,
  }}>
    <div style={{ width: 40, height: 40, borderRadius: 8, background: `${color}15`,
      display: 'flex', alignItems: 'center', justifyContent: 'center', color }}>
      {icon}
    </div>
    <div>
      <div style={{ fontSize: '.75rem', color: '#999', textTransform: 'uppercase', letterSpacing: 1 }}>{label}</div>
      <div style={{ fontSize: '1.3rem', fontWeight: 600, color: '#1a1a2e' }}>{value}</div>
    </div>
  </div>
);

const StatusBadge = ({ status }) => {
  const cfg = STATUS_CONFIG[status] || { label: status, color: '#666', bg: '#f3f4f6' };
  return (
    <span style={{
      display: 'inline-block', padding: '4px 10px', borderRadius: 12,
      fontSize: '.72rem', fontWeight: 500, color: cfg.color, background: cfg.bg,
    }}>{cfg.label}</span>
  );
};

const Th = ({ children, onClick }) => (
  <th onClick={onClick} style={{
    padding: '12px 16px', textAlign: 'left', fontSize: '.72rem',
    textTransform: 'uppercase', letterSpacing: '.5px', color: '#666',
    cursor: onClick ? 'pointer' : 'default', userSelect: 'none', whiteSpace: 'nowrap',
  }}>{children}</th>
);

/* ---------- Shared styles ---------- */

const btnStyle = {
  display: 'inline-flex', alignItems: 'center', gap: 6,
  padding: '8px 16px', borderRadius: 6, fontSize: '.82rem',
  background: '#fff', border: '1px solid #e5e7eb', color: '#333',
  cursor: 'pointer', transition: 'all .15s',
};

const btnSmall = {
  padding: '4px 10px', borderRadius: 4, fontSize: '.75rem',
  background: '#f9fafb', border: '1px solid #e5e7eb', color: '#333',
  cursor: 'pointer',
};

const inputStyle = {
  padding: '8px 12px', borderRadius: 6, border: '1px solid #e5e7eb',
  fontSize: '.85rem', outline: 'none',
};

const labelStyle = {
  display: 'block', fontSize: '.72rem', color: '#999',
  textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 6,
};

const tdStyle = { padding: '12px 16px', verticalAlign: 'top' };

export default CuratorServiceOrders;
